import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetHeader,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetContentBlock,
  ModalSheetFooterSection,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { ErrorBlock } from '@/components/ErrorBlock'
import { byDesign } from '@/infra/design'
import imgInvitePeopleUrl from '@/assets/misc/img_invite-people.svg'
import InviteUsersIllustration from '@/components/SpotIllustration/InviteUsersIllustration'

export interface SubmitJobDialogProps {
  showing: boolean
  requestedProName: string | undefined
  billExternalField?: ReactNode
  isSubmitPending: boolean
  isSubmitRejected: boolean
  submitError: unknown
  onClose: () => unknown
  onSubmit: () => unknown
  onResetSubmit: () => unknown
  testId?: string
  closeButtonTestId?: string
  submitButtonTestId?: string
}

export function SubmitJobDialog({
  showing,
  requestedProName,
  billExternalField,
  isSubmitPending,
  isSubmitRejected,
  submitError,
  onClose,
  onSubmit,
  onResetSubmit,
  testId,
  closeButtonTestId,
  submitButtonTestId,
}: SubmitJobDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock testId={testId}>
          <ModalSheetContentIllustration
            src={imgInvitePeopleUrl}
            illustration={byDesign(undefined, InviteUsersIllustration)}
            alt="Illustration of a girl with her laptop and a plus symbol"
          />
          <ModalSheetContentTitle compact>
            <span>You are about to submit this job</span>
          </ModalSheetContentTitle>
          <ModalSheetContentDescription compact>
            Are you sure you want to submit? <br />
            The job will routed to{' '}
            <b>{requestedProName ?? 'the next available Pro'}.</b>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>

        <ModalSheetContentBlock></ModalSheetContentBlock>
      </ModalSheetContent>

      {isSubmitRejected && (
        <ErrorBlock
          error={submitError || 'Could not submit this job.'}
          onClose={onResetSubmit}
          tackOn="bottom"
        />
      )}

      <ModalSheetFooter>
        {billExternalField && (
          <ModalSheetFooterSection>{billExternalField}</ModalSheetFooterSection>
        )}
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            isDisabled={isSubmitPending}
            variant="default"
            onPress={onClose}
            testId={closeButtonTestId}
          />
          <Button
            label="Submit"
            testId={submitButtonTestId}
            variant="primary"
            onPress={onSubmit}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
